import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import OkoBot from "assets/OKO_op2_yellow.svg";
import OkoBotShadow from "assets/Shadow.svg";
import { colors } from "utils";
import { primaryButtonStyles, PrimaryButtonLabel } from "./PrimaryButton";

// Warm-up empty state (Figma 6178:5920, Body 6178:5945): what an empty class
// shows when warm_up_challenges is on.

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 32px 64px;
  text-align: center;
`;

const Inner = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 600px;
`;

const Title = styled.h2`
  color: #000;
  font-family: "ABC Marfa", sans-serif;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 32px;
  margin: 0;
  text-align: center;
`;

const BotRow = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 8px;
  justify-content: center;
  width: 600px;
`;

const Bot = styled.div`
  flex-shrink: 0;
  height: 108px;
  position: relative;
  width: 115px;
`;

const BotDrawing = styled.img`
  display: block;
  left: 0;
  position: absolute;
  top: 0;
  width: 115px;
  z-index: 1;
`;

const BotShadow = styled.img`
  bottom: 0;
  display: block;
  left: 0;
  position: absolute;
  width: 115px;
`;

const Tooltip = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  width: 363px;
`;

const Tail = styled.svg`
  display: block;
  flex-shrink: 0;
  transform: scaleY(-1);
`;

const Bubble = styled.div`
  align-items: center;
  background: ${colors.blue100};
  border-radius: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 347px;
`;

const BubbleText = styled.p`
  align-self: stretch;
  color: ${colors.nightlyWoods500};
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin: 0;
  text-align: left;
`;

// Figma's 289px; Chrome's Inter metrics would leave it at 288.49.
const PrimaryButton = styled.button`
  ${primaryButtonStyles}
  display: inline-flex;
  padding: 0 16px;
  width: 289px;
`;

// Container gap 24 + 8 = Figma's 32px from the bubble to this row.
const LinkRow = styled.div`
  align-items: center;
  display: flex;
  font-family: Inter, sans-serif;
  font-size: 16px;
  gap: 8px;
  justify-content: center;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin-top: 8px;
`;

const LinkPrompt = styled.span`
  color: #000;
  font-weight: 400;
`;

const LinkButton = styled.button`
  background: transparent;
  border: none;
  color: ${colors.blue700};
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 22px;
  padding: 0;
  text-decoration: none;
`;

const TooltipTail = () => (
  <Tail width="16" height="12" viewBox="0 0 16 12" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 0V12L0 0H16Z" fill={colors.blue100} />
  </Tail>
);

// focusTitle moves focus to the title once, e.g. when archiving the class's
// last warm-up groups brings this state back.
export const WarmUpEmptyState = ({ onPickWarmUp, onSeeAllGroupTypes, style, focusTitle = false, onTitleFocused }) => {
  const titleRef = useRef(null);

  useEffect(() => {
    if (!focusTitle) return;
    titleRef.current?.focus();
    onTitleFocused?.();
  }, [focusTitle, onTitleFocused]);

  return (
    <Container style={style} data-testid="warm-up-empty-state">
      <Inner>
        <Title ref={titleRef} tabIndex={-1}>Get your student talking math!</Title>
        <BotRow>
          <Bot aria-hidden="true">
            <BotDrawing src={OkoBot} alt="" />
            <BotShadow src={OkoBotShadow} alt="" />
          </Bot>
          <Tooltip>
            <TooltipTail />
            <Bubble>
              <BubbleText>
                Warm-Up Challenges need no prior instruction. They are the perfect way to get every student thinking
                and talking about math!
              </BubbleText>
              <PrimaryButton type="button" onClick={onPickWarmUp}>
                <PrimaryButtonLabel>Pick a Warm-Up Challenge</PrimaryButtonLabel>
              </PrimaryButton>
            </Bubble>
          </Tooltip>
        </BotRow>
      </Inner>
      <LinkRow>
        <LinkPrompt>Looking for lesson-aligned groups?</LinkPrompt>
        <LinkButton type="button" onClick={onSeeAllGroupTypes}>
          See all group types
        </LinkButton>
      </LinkRow>
    </Container>
  );
};
